import Chart from 'chart.js/auto';

const fontString = (pixelSize, fontStyle, fontFamily) => {
  return fontStyle + ' ' + pixelSize + 'px ' + fontFamily;
}

const SUPPORTED_TYPES = {};
['pie', 'doughnut', 'polarArea', 'bar', 'horizontalBar'].forEach(function (t) {
  SUPPORTED_TYPES[t] = true
})

function Label() {
  this.renderToDataset = this.renderToDataset.bind(this)
}

Label.prototype.setup = function (chart, options) {
  this.chart = chart
  this.ctx = chart.ctx
  this.args = {}
  this.barTotal = {}
  const chartOptions = chart.config.options
  this.options = Object.assign({
    position: 'default',
    precision: 0,
    fontSize: chartOptions.font ? chartOptions.font.size : 12,
    fontColor: chartOptions.color || '#333333',
    fontStyle: chartOptions.font ? chartOptions.font.style : 'normal',
    fontFamily: chartOptions.font ? chartOptions.font.family : '\'Helvetica Neue\', \'Helvetica\', \'Arial\', sans-serif',
    shadowOffsetX: 3,
    shadowOffsetY: 3,
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowBlur: 6,
    images: [],
    outsidePadding: 2,
    textMargin: 2,
    overlap: true
  }, options)
  if (chart.config.type === 'horizontalBar') {
    this.options.position = 'default'
    this.options.arc = false
    this.options.overlap = true
  }
}

Label.prototype.render = function () {
  this.labelBounds = []
  this.chart.data.datasets.forEach(this.renderToDataset)
}

Label.prototype.renderToDataset = function (dataset, index) {
  this.totalPercentage = 0
  this.total = null
  const arg = this.args[index]
  arg.meta.data.forEach(function (element, index) {
    this.renderToElement(dataset, arg, element, index)
  }.bind(this))
}

Label.prototype.renderToElement = function (dataset, arg, element, index) {
  if (!this.shouldRenderToElement(arg.meta, element)) {
    return
  }
  this.percentage = null
  const label = this.getLabel(dataset, element, index)
  if (!label) {
    return
  }
  const ctx = this.ctx
  ctx.save()
  ctx.font = fontString(this.options.fontSize, this.options.fontStyle, this.options.fontFamily)
  const renderInfo = this.getRenderInfo(element, label)
  if (!this.drawable(element, label, renderInfo)) {
    ctx.restore()
    return
  }
  ctx.beginPath()
  ctx.fillStyle = this.getFontColor(dataset, element, index)
  this.renderLabel(label, renderInfo, element)
  this.renderLine(label, element, renderInfo)
  ctx.restore()
}

Label.prototype.renderLabel = function (label, renderInfo, element) {
  return this.options.arc ? this.renderArcLabel(label, renderInfo) : this.renderBaseLabel(label, renderInfo, element)
}

Label.prototype.renderLine = function (label, element, renderInfo) {
  const ctx = this.ctx
  ctx.beginPath()
  ctx.strokeStyle = element.options.backgroundColor
  const startPoint = element.getCenterPoint()
  const centreAngle = element.startAngle + (element.endAngle - element.startAngle) / 2
  ctx.moveTo(startPoint.x + Math.cos(centreAngle) * 7, startPoint.y + Math.sin(centreAngle) * 7)
  ctx.lineTo(renderInfo.x, renderInfo.y)
  if (this.chart.config.type === 'doughnut') {
    const metrics = ctx.measureText(label[0].text)
    if (element.x > renderInfo.x) {
      ctx.lineTo(renderInfo.x - metrics.width - 5, renderInfo.y)
    } else {
      ctx.lineTo(renderInfo.x + metrics.width + 5, renderInfo.y)
    }
  }
  ctx.stroke()
}

Label.prototype.renderBaseLabel = function (label, position, element) {
  const ctx = this.ctx
  ctx.save()
  let extraHeight = 0
  if (this.chart.config.type === 'horizontalBar') {
    ctx.textBaseline = (element.$context.dataIndex % 2 === 1) ? 'bottom' : 'top'
    ctx.textAlign = 'left'
    extraHeight = (element.$context.dataIndex % 2 === 1) ? -5 : 5
  } else {
    ctx.textBaseline = 'bottom'
    ctx.textAlign = element.x > position.x ? 'right' : 'left'
  }

  if (this.options.textShadow) {
    ctx.shadowOffsetX = this.options.shadowOffsetX
    ctx.shadowOffsetY = this.options.shadowOffsetY
    ctx.shadowColor = this.options.shadowColor
    ctx.shadowBlur = this.options.shadowBlur
  }

  const lines = label
  for (let i = 0; i < lines.length; i++) {
    const y = position.y - this.options.fontSize / 2 * lines.length + ((this.options.fontSize + 5) * i)
    if (lines[i].fontWeight) {
      ctx.font = Chart.helpers.fontString(this.options.fontSize, lines[i].fontWeight, this.options.fontFamily)
    }
    ctx.fillText(lines[i].text, position.x + (ctx.textAlign === 'right' ? -3 : +3), y + 5 + extraHeight)
  }
  ctx.restore()
}

Label.prototype.renderArcLabel = function (label, renderInfo) {
}

Label.prototype.shouldRenderToElement = function (meta, element) {
  return !meta.hidden && !element.hidden && (
    this.options.showZero ||
    this.chart.config.type === 'polarArea'
      ? element.outerRadius !== 0
      : element.circumference !== 0
  )
}

Label.prototype.getLabel = function (dataset, element, index) {
  let label
  if (typeof this.options.render === 'function') {
    label = this.options.render({
      label: this.chart.config.data.labels[index],
      value: dataset.data[index],
      percentage: this.getPercentage(dataset, element, index),
      dataset: dataset,
      index: index
    })
  } else {
    switch (this.options.render) {
      case 'value':
        label = dataset.data[index]
        break
      case 'label':
        label = this.chart.config.data.labels[index]
        break
      case 'image':
        label = this.options.images[index] ? this.loadImage(this.options.images[index]) : ''
        break
      case 'percentage':
      default:
        label = this.getPercentage(dataset, element, index) + '%'
        break
    }
  }
  return label
}

Label.prototype.getFontColor = function (dataset, element, index) {
  let fontColor = this.options.fontColor
  if (typeof fontColor === 'function') {
    fontColor = fontColor({
      label: this.chart.config.data.labels[index],
      value: dataset.data[index],
      percentage: this.getPercentage(dataset, element, index),
      backgroundColor: dataset.backgroundColor[index],
      dataset: dataset,
      index: index
    })
  } else if (typeof fontColor !== 'string') {
    fontColor = fontColor && fontColor[index] || this.chart.config.options.defaultFontColor
  }
  return fontColor
}

Label.prototype.getPercentage = function (dataset, element, index) {
  if (this.percentage !== null) {
    return this.percentage
  }
  let percentage
  if (this.chart.config.type === 'polarArea') {
    if (this.total === null) {
      this.total = 0
      for (let i = 0; i < dataset.data.length; ++i) {
        this.total += dataset.data[i]
      }
    }
    percentage = dataset.data[index] / this.total * 100
  } else if (this.chart.config.type === 'horizontalBar') {
    if (this.barTotal[index] === undefined) {
      this.barTotal[index] = 0
      for (let i = 0; i < this.chart.data.datasets.length; ++i) {
        this.barTotal[index] += this.chart.data.datasets[i].data[index]
      }
    }
    percentage = dataset.data[index] / this.barTotal[index] * 100
  } else {
    percentage = element.circumference / this.chart.config.options.circumference * 100
  }
  percentage = parseFloat(percentage.toFixed(this.options.precision))
  if (!this.options.showActualPercentages) {
    if (this.chart.config.type === 'horizontalBar') {
      this.totalPercentage = this.barTotalPercentage[index] || 0
    }
    this.totalPercentage += percentage
    if (this.totalPercentage > 100) {
      percentage -= this.totalPercentage - 100
      percentage = parseFloat(percentage.toFixed(this.options.precision))
    }
    if (this.chart.config.type === 'horizontalBar') {
      this.barTotalPercentage[index] = this.totalPercentage
    }
  }
  this.percentage = percentage
  return percentage
}

Label.prototype.getRenderInfo = function (element, label) {
  if (this.chart.config.type === 'horizontalBar') {
    return this.getBarRenderInfo(element, label)
  } else {
    return this.options.arc ? this.getArcRenderInfo(element, label) : this.getBaseRenderInfo(element, label)
  }
}

Label.prototype.getBaseRenderInfo = function (element, label) {
  if (this.options.position === 'outside' || this.options.position === 'border') {
    let renderInfo
    let rangeFromCentre
    const centreAngle = element.startAngle + (element.endAngle - element.startAngle) / 2
    const innerRadius = element.outerRadius / 2
    if (this.options.position === 'border') {
      rangeFromCentre = (element.outerRadius - innerRadius) / 2 + innerRadius
    } else if (this.options.position === 'outside') {
      rangeFromCentre = (element.outerRadius - innerRadius) + innerRadius + this.options.textMargin
    }
    renderInfo = {
      x: element.x + (Math.cos(centreAngle) * rangeFromCentre),
      y: element.y + (Math.sin(centreAngle) * rangeFromCentre)
    }
    if (renderInfo.y >= element.y) {
      renderInfo.y += this.options.fontSize + 3
    }
    if (renderInfo.y < element.y) {
      renderInfo.y -= 3
    }
    if ((element.endAngle - element.startAngle) < 0.5) {
      if (element.x > renderInfo.x) {
        renderInfo.x -= 10
      } else {
        renderInfo.x += 10
      }
    }

    return renderInfo
  } else {
    return element.tooltipPosition()
  }
}

Label.prototype.getArcRenderInfo = function (element, label) {
  let radius
  if (this.options.position === 'outside') {
    radius = element.outerRadius + this.options.fontSize + this.options.textMargin
  } else if (this.options.position === 'border') {
    radius = (element.outerRadius / 2 + element.outerRadius) / 2
  } else {
    radius = (element.innerRadius + element.outerRadius) / 2
  }
  let startAngle = element.startAngle
  let endAngle = element.endAngle
  const totalAngle = endAngle - startAngle
  startAngle += Math.PI / 2
  endAngle += Math.PI / 2
  const mertrics = this.measureLabel(label)
  startAngle += (endAngle - (mertrics.width / radius + startAngle)) / 2
  return {
    radius: radius,
    startAngle: startAngle,
    endAngle: endAngle,
    totalAngle: totalAngle,
    view: element
  }
}

Label.prototype.getBarRenderInfo = function (element, label) {
  const renderInfo = element.getCenterPoint()
  if (element._datasetIndex % 2 === 1) {
    renderInfo.y -= this.measureLabel(label).height / 2 + this.options.textMargin
  } else {
    renderInfo.y += this.measureLabel(label).height / 2 + this.options.textMargin
  }
  return renderInfo
}

Label.prototype.drawable = function (element, label, renderInfo) {
  if (this.options.overlap) {
    return true
  } else if (this.options.arc) {
    return renderInfo.endAngle - renderInfo.startAngle <= renderInfo.totalAngle
  } else {
    const mertrics = this.measureLabel(label)
    const left = renderInfo.x - mertrics.width / 2
    const right = renderInfo.x + mertrics.width / 2
    const top = renderInfo.y - mertrics.height / 2
    const bottom = renderInfo.y + mertrics.height / 2
    if (this.options.renderInfo === 'outside') {
      return this.outsideInRange(left, right, top, bottom)
    } else {
      return element.inRange(left, top) && element.inRange(left, bottom) &&
        element.inRange(right, top) && element.inRange(right, bottom)
    }
  }
}

Label.prototype.outsideInRange = function (left, right, top, bottom) {
  const labelBounds = this.labelBounds
  for (let i = 0; i < labelBounds.length; ++i) {
    const bound = labelBounds[i]
    let potins = [
      [left, top],
      [left, bottom],
      [right, top],
      [right, bottom]
    ]
    for (let j = 0; j < potins.length; ++j) {
      const x = potins[j][0]
      const y = potins[j][1]
      if (x >= bound.left && x <= bound.right && y >= bound.top && y <= bound.bottom) {
        return false
      }
    }
    potins = [
      [bound.left, bound.top],
      [bound.left, bound.bottom],
      [bound.right, bound.top],
      [bound.right, bound.bottom]
    ]
    for (let j = 0; j < potins.length; ++j) {
      const x = potins[j][0]
      const y = potins[j][1]
      if (x >= left && x <= right && y >= top && y <= bottom) {
        return false
      }
    }
  }
  labelBounds.push({
    left: left,
    right: right,
    top: top,
    bottom: bottom
  })
  return true
}

Label.prototype.measureLabel = function (label) {
  let width = 0
  const lines = label
  for (let i = 0; i < lines.length; ++i) {
    const result = this.ctx.measureText(lines[i].text)
    if (result.width > width) {
      width = result.width
    }
  }
  return {width: width, height: this.options.fontSize * lines.length}
}

Label.prototype.loadImage = function (obj) {
  const image = new Image()
  image.src = obj.src
  image.width = obj.width
  image.height = obj.height
  return image
}

const useOuterLabelPlugin = () => {
  return {
    id: 'labels',
    beforeDatasetsUpdate: function (chart, args, options) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return
      }
      if (!Array.isArray(options)) {
        options = [options]
      }
      const count = options.length
      if (!chart._labels || count !== chart._labels.length) {
        chart._labels = options.map(function () {
          return new Label()
        })
      }
      let someOutside = false
      let maxPadding = 0
      for (let i = 0; i < count; ++i) {
        const label = chart._labels[i]
        label.setup(chart, options[i])
        if (label.options.position === 'outside') {
          someOutside = true
          const padding = label.options.fontSize * 1.5 + label.options.outsidePadding
          if (padding > maxPadding) {
            maxPadding = padding
          }
        }
      }
      if (someOutside) {
        chart.chartArea.top += maxPadding
        chart.chartArea.bottom -= maxPadding
      }
    },
    afterDatasetUpdate: function (chart, args) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return
      }
      chart._labels.forEach(function (label) {
        label.args[args.index] = args
      })
    },
    beforeDraw: function (chart) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return
      }
      chart._labels && chart._labels.forEach(function (label) {
        label.barTotalPercentage = {}
      })
    },
    afterDatasetsDraw: function (chart) {
      if (!SUPPORTED_TYPES[chart.config.type]) {
        return
      }
      chart._labels && chart._labels.forEach(function (label) {
        label.render()
      })
    }
  }
}

export default useOuterLabelPlugin
